// #666 — best-effort weekday/time extraction from a free-text activity fact.
//
// family_context activities (category='kid', key='activity') are captured by
// kin-chat's [LEARN] pipeline as a single free-text string, e.g.
// "soccer Tuesdays at 4pm" — there is no structured weekday/time field. This
// parses that prose at READ time (never rewrites the stored fact) so Today
// can place an activity on the correct weekday.
//
// Fails closed on purpose: if no weekday can be confidently identified,
// callers should exclude the activity from Today rather than guess wrong or
// default it to every day. A missed activity is a smaller harm than one
// shown on the wrong day or every day.

export interface ParsedActivityRecurrence {
  /** 0=Sun..6=Sat, ascending, sorted, may be empty if unparseable. */
  weekdays: number[];
  /** "HH:MM" 24h, or null if no clock time was captured (render as all-day). */
  time: string | null;
}

const WEEKDAY_TOKENS: Array<{ day: number; tokens: string[] }> = [
  { day: 0, tokens: ["sunday", "sun"] },
  { day: 1, tokens: ["monday", "mon"] },
  { day: 2, tokens: ["tuesday", "tues", "tue"] },
  { day: 3, tokens: ["wednesday", "weds", "wed"] },
  { day: 4, tokens: ["thursday", "thurs", "thur", "thu"] },
  { day: 5, tokens: ["friday", "fri"] },
  { day: 6, tokens: ["saturday", "sat"] },
];

// #789 — hoisted out of parseActivityRecurrence so the write-back helper below
// can find the SAME token the parser will read back, rather than keeping a
// second copy of these patterns that quietly drifts. Behavior is unchanged:
// same literals, no /g flag, so no shared lastIndex state.
const AMPM_TIME_RE = /\b(\d{1,2})(?::([0-5]\d))?\s*(am|pm)\b/i;
const H24_TIME_RE = /\b([01]?\d|2[0-3]):([0-5]\d)\b/;

export function parseActivityRecurrence(value: string): ParsedActivityRecurrence {
  const text = (value || "").toLowerCase();

  const weekdays: number[] = [];
  for (const { day, tokens } of WEEKDAY_TOKENS) {
    if (tokens.some((token) => new RegExp(`\\b${token}s?\\b`, "i").test(text))) {
      weekdays.push(day);
    }
  }
  weekdays.sort((a, b) => a - b);

  let time: string | null = null;
  const ampmMatch = text.match(AMPM_TIME_RE);
  if (ampmMatch) {
    let hour = parseInt(ampmMatch[1], 10);
    const minute = ampmMatch[2] || "00";
    const meridiem = ampmMatch[3].toLowerCase();
    if (hour === 12) hour = 0;
    if (meridiem === "pm") hour += 12;
    if (hour >= 0 && hour <= 23) {
      time = `${String(hour).padStart(2, "0")}:${minute}`;
    }
  } else {
    // Bare 24h "HH:MM" only — no bare-hour fallback (a lone digit like "4"
    // in free text is more likely an age or a count than a time, and a
    // wrong time is worse than no time here since it decides whether the
    // activity renders at a clock time or as an all-day band).
    const h24Match = text.match(H24_TIME_RE);
    if (h24Match) {
      time = `${h24Match[1].padStart(2, "0")}:${h24Match[2]}`;
    }
  }

  return { weekdays, time };
}

export interface ActivityDayLike {
  date?: string;
  day_name: string;
}

export interface ActivityFactLike {
  id: string;
  subject: string | null;
  value: string;
}

export interface ActivityRow {
  key: string;
  factId: string;
  dayLabel: string;
  date: string;
  time: string | null;
  subject: string | null;
  value: string;
}

/**
 * Cross the 7-day window against every activity fact, keeping only the
 * (day, activity) pairs where the parsed weekday actually matches that day.
 * An activity that fails to parse (empty weekdays) never produces a row —
 * see the fail-closed note above parseActivityRecurrence.
 */
export function buildActivityRows(days: ActivityDayLike[], activities: ActivityFactLike[]): ActivityRow[] {
  const rows: ActivityRow[] = [];
  for (const day of days) {
    if (!day.date) continue;
    const jsWeekday = new Date(`${day.date}T00:00:00`).getDay();
    for (const fact of activities) {
      const parsed = parseActivityRecurrence(fact.value);
      if (!parsed.weekdays.includes(jsWeekday)) continue;
      rows.push({
        key: `${fact.id}-${day.date}`,
        factId: fact.id,
        dayLabel: day.day_name,
        date: day.date,
        time: parsed.time,
        subject: fact.subject,
        value: fact.value,
      });
    }
  }
  return rows;
}

// ---------------------------------------------------------------------------
// #789 — telling her when a fact can't be placed, and letting her fix it.
//
// Nothing below changes the parser, its threshold, or the fail-closed drop
// above. It reads the SAME functions to answer a different question: "which
// stored facts is Today silently skipping?" — so Family Profile can say so
// where she is already standing, instead of the activity just never appearing.
// ---------------------------------------------------------------------------

export const WEEKDAY_LABELS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

export interface StoredFactLike {
  category: string;
  key: string;
  value: string;
}

/**
 * Would the Today activity layer store this fact and then render nothing?
 *
 * Mirrors two things exactly, and must keep mirroring them:
 *   - `useWeeklyActivities`' query filter — category 'kid', key 'activity'
 *   - `buildActivityRows`' fail-closed drop — no weekday means no row
 *
 * If either moves and this doesn't, the marker starts pointing at facts that
 * render fine, or (worse) stays quiet about ones that don't — which is the
 * exact silence this exists to end.
 */
export function isUnresolvedActivityFact(fact: StoredFactLike): boolean {
  if (fact.category !== "kid" || fact.key !== "activity") return false;
  return parseActivityRecurrence(fact.value).weekdays.length === 0;
}

/** "16:00" -> "4pm", "17:30" -> "5:30pm", "00:00" -> "12am". */
export function formatClockForProse(time: string): string {
  const [rawHour, rawMinute] = (time || "").split(":");
  const hour = Number(rawHour);
  const minute = Number(rawMinute);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return "";
  const period = hour >= 12 ? "pm" : "am";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return minute === 0
    ? `${hour12}${period}`
    : `${hour12}:${String(minute).padStart(2, "0")}${period}`;
}

/** [2] -> "Tuesdays"; [1,3] -> "Mondays and Wednesdays". */
export function formatWeekdayList(weekdays: number[]): string {
  const names = weekdays.map((d) => `${WEEKDAY_LABELS[d]}s`);
  if (names.length <= 1) return names[0] || "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** Swap the first time token the parser would read, leaving her other words alone. */
function replaceFirstTimeToken(text: string, replacement: string): string {
  if (AMPM_TIME_RE.test(text)) return text.replace(AMPM_TIME_RE, replacement);
  return text.replace(H24_TIME_RE, replacement);
}

/**
 * Fold a chosen day (and optional time) back into the fact's own prose, in a
 * form `parseActivityRecurrence` reads back as exactly those values.
 *
 * Her words are kept and the schedule is appended — the stored value is the
 * one string Today renders, so rewriting it wholesale would put Kin's phrasing
 * on screen in place of hers.
 *
 * This is only offered on facts `isUnresolvedActivityFact` returned true for,
 * which is what makes the round-trip safe: such a fact contains NO weekday
 * token, so the appended clause alone decides the parsed weekdays. Time needs
 * one more step, because the parser takes the FIRST time in the string: if her
 * text already names a different one, appending would be ignored, so that one
 * token is replaced in place instead.
 */
export function applyActivitySchedule(
  value: string,
  weekdays: number[],
  time: string | null
): string {
  const days = Array.from(new Set(weekdays.filter((d) => d >= 0 && d <= 6))).sort((a, b) => a - b);
  if (days.length === 0) return value;

  // Trailing punctuation would otherwise read as "soccer practice, — Tuesdays".
  let base = (value || "").trim().replace(/[\s,;.:–—-]+$/g, "");
  let clause = formatWeekdayList(days);

  if (time) {
    const existing = parseActivityRecurrence(base).time;
    if (existing === time) {
      // Already in her own words — don't say it twice.
    } else if (existing !== null) {
      base = replaceFirstTimeToken(base, formatClockForProse(time));
    } else {
      clause += ` at ${formatClockForProse(time)}`;
    }
  }

  return base ? `${base} — ${clause}` : clause;
}

/**
 * One row per ACTIVITY rather than per occurrence, for the month view (#788).
 *
 * ⚠️ Why this exists instead of reusing buildActivityRows directly.
 *
 * buildActivityRows expands an activity onto every matching day, which is
 * right for a week — soccer on Tuesdays is one row. Over a month it is four or
 * five near-identical rows, and three activities become ~15 lines of noise
 * above the grid. That is worse than showing nothing, which is the bar this
 * had to clear.
 *
 * So the month band collapses each activity to its cadence. `dayLabel` becomes
 * "Every Tuesday" (or "Tuesday & Thursday" when it recurs on several), which
 * is unambiguous without pluralising a label the caller supplied — day_name is
 * a full weekday name, but this must not break if that ever changes.
 *
 * Order is preserved from the input, so the band lists activities in the order
 * their first occurrence falls in the range.
 */
export interface ActivityCadenceRow {
  key: string;
  factId: string;
  /** "Every Tuesday" / "Tuesday & Thursday". */
  dayLabel: string;
  time: string | null;
  subject: string | null;
  value: string;
}

export function buildActivityCadenceRows(rows: ActivityRow[]): ActivityCadenceRow[] {
  const byFact = new Map<string, { row: ActivityRow; days: string[] }>();
  for (const row of rows) {
    const existing = byFact.get(row.factId);
    if (!existing) {
      byFact.set(row.factId, { row, days: [row.dayLabel] });
      continue;
    }
    // Distinct days only — the same weekday recurs several times in a month.
    if (!existing.days.includes(row.dayLabel)) existing.days.push(row.dayLabel);
  }

  return [...byFact.values()].map(({ row, days }) => ({
    key: `cadence-${row.factId}`,
    factId: row.factId,
    dayLabel: days.length === 1 ? `Every ${days[0]}` : days.join(" & "),
    time: row.time,
    subject: row.subject,
    value: row.value,
  }));
}

